/** Retry wiring is local to the existing actions; ownership and clearing live in the lifecycle. */
import type { UnifiedStudySetupPanelBuilder } from './UnifiedStudySetupPanel'
import { RequestFailureBanner } from '../RequestFailureBanner'
import { PageNotFound } from '../PageNotFound'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { RequestFailure } from '../../page-feedback'
import type { PageActionName } from '../../page-declaration'
import type { page } from '../../pages/study-builder.page'
import { resolveEditSections } from './workingPhrase'

/** Only an actual evaluation failure belongs beside its attempted edits; other operations keep declared locations. */
export function evaluationFailureSections(builder: UnifiedStudySetupPanelBuilder): ReadonlySet<string> {
  if (!builder.requestFailures.some((failure) => failure.operation === 'evaluate-change')) return new Set()
  return resolveEditSections(builder.evaluation.projection?.sections ?? [], builder.evaluation.failedEdits)
}

export function StudyBuilderRequestFailures({
  builder,
  location,
  sectionId,
  onBack,
}: {
  builder: UnifiedStudySetupPanelBuilder
  location: RequestFailure['location']
  sectionId?: string
  /** No editor is available yet; use the shared full-page treatment and the same recovery owner. */
  onBack?: () => void
}) {
  // What a plain retry re-sends for each action. Only used when retrying could actually succeed (a network blip).
  const retry = {
    'choose-product': builder.handleOpenProductPicker,
    'open-guidance': undefined,
    'upload-image': undefined,
    'evaluate-change': builder.evaluation.retry,
    'save-draft': builder.handleSaveDraft,
    discard: builder.handleDiscard,
    'run-study': builder.handleRunStudyFromBuilder,
    'delete-setup': undefined,
  } satisfies Record<PageActionName<typeof page>, (() => unknown) | undefined>
  // The recovery offered comes from what went wrong, not a fixed button:
  //  - conflict: retrying sends the same stale revision, so load the saved version instead (reuses the newer-version path).
  //  - network/timeout: retrying the same request can succeed, so offer Try again.
  //  - anything else: retrying cannot help and there is no safe recovery here, so show no button.
  const recovery = (f: RequestFailure): { onRetry?: () => void | Promise<unknown>; actionLabel?: string } => {
    if (f.kind === 'conflict')
      return {
        onRetry: builder.handleLoadSavedVersion,
        actionLabel: shopifyComponentCopy.studyBuilderPage.loadSavedVersion,
      }
    if (f.kind === 'network')
      return {
        onRetry: retry[f.operation as PageActionName<typeof page>],
        actionLabel: shopifyComponentCopy.studySetupCard.tryAgain026,
      }
    return {}
  }
  // When an error carried no server wording of its own (a network blip that never reached the Worker, or a
  // browser-side integrity error), the notice shows the Worker's offline words — `readiness.onReadFailure` once an
  // evaluation has answered, else the connected status's load-error `network` body (present before any projection).
  // The app never substitutes a constant of its own; a failure with no resolvable words shows no banner rather than
  // invent copy.
  const offlineWords =
    builder.evaluation.projection?.readiness.onReadFailure.help ?? builder.readiness?.builderLoadError?.network.body
  const sections = evaluationFailureSections(builder)
  return (builder.requestFailures ?? [])
    .filter((f) => {
      if (f.location !== location) return false
      if (f.operation !== 'evaluate-change') return sectionId === undefined
      return sectionId === undefined ? sections.size === 0 : sections.has(sectionId)
    })
    .map((f) => {
      const message = f.message ?? offlineWords
      if (!message) return null
      const { onRetry, actionLabel } = recovery(f)
      const button = builder.lifecycle.button(f.operation as PageActionName<typeof page>)
      const disabled = f.pending || button.disabled || button.loading
      if (onBack) {
        const words = builder.readiness?.builderLoadError?.[f.kind]
        if (!words) throw new Error('STUDY_BUILDER_LOAD_ERROR_WORDS_MISSING')
        const action =
          onRetry && actionLabel ? { content: actionLabel, onAction: () => void onRetry(), disabled } : undefined
        return (
          <PageNotFound
            key={f.operation}
            heading={words.title}
            body={message}
            errorName={f.kind}
            onBack={onBack}
            action={action}
          />
        )
      }
      return (
        <RequestFailureBanner
          key={f.operation}
          message={message}
          pending={f.pending}
          disabled={disabled}
          onRetry={onRetry}
          actionLabel={actionLabel}
        />
      )
    })
}
